import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import {
  KoobAlert,
  KoobButton,
  KoobInputSelect,
  KoobModal,
  KoobRichTextEditor,
} from '@koob/ui';
import FolderFormTextField from './FolderFormTextField';
import {
  getFolderMainGuest,
  getFolderStatus,
  isFolderChildTraveler,
} from '@koob/utils';
import FolderStatusBadge from './FolderStatusBadge';
import type {
  FolderActionFolder,
  FolderActionTraveler,
  FolderEditDetailsValues,
} from './types/folderActions';

export interface FolderEditDetailsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  folder: FolderActionFolder;
  onSubmit: (values: FolderEditDetailsValues) => Promise<void> | void;
}

interface FormValues {
  name: string;
  icr: string;
  note: string;
  mainGuestId: string | null;
}

// Labels of the mockup: small uppercase captions above each field.
const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-gray-500">
      {label}
    </span>
    {children}
  </div>
);

const travelerLabel = (traveler: FolderActionTraveler) =>
  `${traveler?.firstName ?? ''} ${traveler?.lastName ?? ''}`.trim() || '--';

/**
 * KD-3842: folder name, ref, main guest and internal note; the status is
 * read-only (derived from the bookings). Only adults can be main guest, and
 * mainTravelerId is sent only when it changes, so the default (first adult)
 * is not frozen into an explicit choice. Shared by the TO and DMC apps.
 */
export default function FolderEditDetailsModal({
  open,
  setOpen,
  folder,
  onSubmit,
}: FolderEditDetailsModalProps) {
  const { t } = useTranslation('folders');

  const mainGuestOptions = [...(folder?.travelers ?? [])]
    .filter((traveler) => !isFolderChildTraveler(traveler))
    .sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0))
    .map((traveler) => ({
      value: String(traveler.id),
      label: travelerLabel(traveler),
    }));
  const defaultMainGuest = getFolderMainGuest(folder);
  const initialMainGuestId = defaultMainGuest
    ? String(defaultMainGuest.id)
    : null;
  const hasBookings = (folder?.bookings ?? []).length > 0;

  const handleSubmit = async (values: FormValues) => {
    await onSubmit({
      name: values.name,
      icr: values.icr,
      note: values.note,
      ...(values.mainGuestId !== initialMainGuestId
        ? { mainTravelerId: values.mainGuestId }
        : {}),
    });
  };

  return (
    <KoobModal
      open={open}
      setOpen={setOpen}
      widthClass="sm:max-w-[560px]"
      paddingClass="p-0"
      autoFocus={false}
    >
      <Formik<FormValues>
        initialValues={{
          name: folder?.name ?? '',
          icr: folder?.icr ?? '',
          note: folder?.note ?? '',
          mainGuestId: initialMainGuestId,
        }}
        enableReinitialize
        onSubmit={handleSubmit}
        validate={(values) => {
          const errors: Partial<Record<keyof FormValues, string>> = {};
          if (!values.name.trim()) errors.name = t('errors:required');
          if (!values.icr.trim()) errors.icr = t('errors:required');
          return errors;
        }}
      >
        {({ values, setFieldValue, dirty, isSubmitting, isValid }) => (
          <Form>
            <div className="flex items-center gap-2.5 border-0 border-b border-solid border-gray-100 px-[22px] py-[18px]">
              <h2 className="m-0 flex-1 text-base font-bold text-k-h">
                {t('editDetails.title')}
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t('misc:close')}
                className="cursor-pointer border-none bg-transparent p-0 text-lg leading-none text-gray-400 hover:text-gray-600"
              >
                <i className="fa-regular fa-xmark" aria-hidden="true" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 p-[22px] sm:grid-cols-2">
              <Field label={t('editDetails.folderName')}>
                <FolderFormTextField name="name" testId="edit-folder-name" />
              </Field>
              <Field label={t('editDetails.folderRef')}>
                <FolderFormTextField name="icr" testId="edit-folder-ref" />
              </Field>
              <Field label={t('editDetails.mainGuest')}>
                <KoobInputSelect
                  options={mainGuestOptions}
                  value={values.mainGuestId}
                  disabled={mainGuestOptions.length === 0}
                  onValueChange={(value: string) =>
                    setFieldValue('mainGuestId', value ?? null)
                  }
                  data-testid="edit-folder-main-guest"
                />
              </Field>
              <Field label={t('editDetails.status')}>
                <div className="flex h-10 items-center rounded-[10px] border border-solid border-gray-100 bg-gray-50 px-3">
                  <FolderStatusBadge status={getFolderStatus(folder)} />
                  <span className="ml-2.5 text-[11.5px] text-gray-400">
                    {t('editDetails.statusHint')}
                  </span>
                </div>
              </Field>
              {hasBookings && values.mainGuestId !== initialMainGuestId && (
                <div className="sm:col-span-2">
                  <KoobAlert
                    variant="info"
                    icon="fa-circle-info"
                    description={t('editDetails.roomLeaderInfo')}
                  />
                </div>
              )}
              {/* Same rich text as the booking tunnel "Notes", which fills it. */}
              <div className="sm:col-span-2">
                <Field label={t('editDetails.internalNote')}>
                  <KoobRichTextEditor
                    value={values.note}
                    onChange={(note) => setFieldValue('note', note)}
                    placeholder={t('editDetails.internalNotePlaceholder')}
                    minHeight="5em"
                  />
                </Field>
              </div>
            </div>

            <div className="flex justify-end gap-2.5 border-0 border-t border-solid border-gray-100 bg-gray-50 px-[22px] py-4">
              <KoobButton
                type="button"
                variant="secondary"
                onClick={() => setOpen(false)}
                data-testid="edit-folder-cancel"
              >
                {t('misc:cancel')}
              </KoobButton>
              <KoobButton
                type="submit"
                variant="primary"
                disabled={!dirty || isSubmitting || !isValid}
                data-testid="save-folder-details"
              >
                {t('misc:saveChanges')}
              </KoobButton>
            </div>
          </Form>
        )}
      </Formik>
    </KoobModal>
  );
}
